// =============================================================================
// MeuDriver — LearningPathTimeline: Trilha de aprendizado do estudante
// =============================================================================

import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CheckCircle, Lock, ArrowRight } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useLearningMilestones, useStudentMilestoneProgress } from '@/hooks/useLearningPath'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAchievedAt(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "d 'de' MMM. 'de' yyyy", { locale: ptBR })
  } catch {
    return dateStr
  }
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function LearningPathTimeline() {
  const { data: milestones, isLoading: loadingMilestones } = useLearningMilestones()
  const { data: progress, isLoading: loadingProgress } = useStudentMilestoneProgress()

  const isLoading = loadingMilestones || loadingProgress

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2 pt-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!milestones?.length) return null

  const achievedSet = new Set<string>(
    (progress ?? []).map((p) => p.milestone_id),
  )
  const achievedCount = achievedSet.size
  const totalCount = milestones.length

  // Índice do próximo não-alcançado
  const nextIndex = milestones.findIndex((m) => !achievedSet.has(m.id))

  return (
    <div className="space-y-1">
      {/* Barra de progresso geral */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-800">
          {achievedCount} de {totalCount} etapa{totalCount !== 1 ? 's' : ''} concluída{totalCount !== 1 ? 's' : ''}
        </p>
        <span className="text-xs text-muted-foreground">
          {Math.round((achievedCount / totalCount) * 100)}%
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-5">
        <div
          className="h-full rounded-full bg-gradient-to-r from-brand-500 to-teal-500 transition-all duration-700"
          style={{ width: `${Math.round((achievedCount / totalCount) * 100)}%` }}
        />
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Linha vertical conectora */}
        <div
          className="absolute left-5 top-5 w-0.5 bg-gray-200"
          style={{ height: `calc(100% - 40px)` }}
        />

        <div className="space-y-0">
          {milestones.map((milestone, idx) => {
            const achieved = achievedSet.has(milestone.id)
            const isNext = idx === nextIndex
            const achievedRecord = (progress ?? []).find(
              (p) => p.milestone_id === milestone.id,
            )

            return (
              <div key={milestone.id} className="relative flex gap-4 pb-6 last:pb-0">
                {/* Ícone / status */}
                <div className="relative z-10 shrink-0">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center text-base border-2 transition-all ${
                      achieved
                        ? 'bg-teal-500 border-teal-500 text-white shadow-sm'
                        : isNext
                          ? 'bg-brand-50 border-brand-400 text-brand-600 ring-2 ring-brand-200'
                          : 'bg-white border-gray-200 text-gray-400'
                    }`}
                  >
                    {achieved ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : isNext ? (
                      <span className="text-base">{milestone.badge_icon}</span>
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {/* Conteúdo */}
                <div className="flex-1 pt-1.5 pb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      className={`text-sm font-semibold ${
                        achieved
                          ? 'text-teal-700'
                          : isNext
                            ? 'text-brand-700'
                            : 'text-gray-400'
                      }`}
                    >
                      {achieved && (
                        <span className="mr-1">{milestone.badge_icon}</span>
                      )}
                      {milestone.title}
                    </p>

                    {isNext && !achieved && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 bg-brand-50 border border-brand-200 rounded-full px-2 py-0.5">
                        <ArrowRight className="h-3 w-3" />
                        Próxima etapa
                      </span>
                    )}
                  </div>

                  {milestone.description && (
                    <p className={`text-xs mt-0.5 ${achieved ? 'text-teal-600' : 'text-muted-foreground'}`}>
                      {milestone.description}
                    </p>
                  )}

                  {achieved && achievedRecord && (
                    <p className="text-xs text-teal-500 mt-1">
                      Conquistado em {formatAchievedAt(achievedRecord.achieved_at)}
                    </p>
                  )}

                  {!achieved && isNext && milestone.requirement_type === 'lessons_count' && (
                    <p className="text-xs text-brand-500 mt-1">
                      Precisa de {milestone.requirement_value} aula{milestone.requirement_value !== 1 ? 's' : ''} concluída{milestone.requirement_value !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
